import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { CheckCircle2, Clock, Inbox, ListChecks } from "lucide-react";
import { StudentShell } from "../components/StudentShell";
import { Modal } from "../components/Shell";
import { SheetSolver } from "../components/Worksheet";
import { useLang } from "../i18n";
import { api } from "../api";
import { sfx } from "../sfx";

export default function Tasks() {
  const { t } = useLang();
  const [items, setItems] = useState([]);
  const [target, setTarget] = useState(null);
  const [answer, setAnswer] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const load = () => api.get("/assignments").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    await api.post("/submissions", { assignment_id: target.id, answer });
    sfx.points();
    setTarget(null); setAnswer(""); toast.success("تم إرسال الحل للمعلم"); load();
  };

  const submitSheet = async (answers) => {
    setBusy(true);
    try {
      const { data } = await api.post("/submissions/sheet", {
        assignment_id: target.id, answers,
      });
      setResult(data); sfx.points();
      toast.success(data.auto_total
        ? `تم التسليم — ${data.auto_correct} من ${data.auto_total} صحيحة`
        : "تم التسليم، بانتظار تصحيح المعلم");
      load();
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "تعذّر التسليم");
    } finally { setBusy(false); }
  };

  const badge = (s) => ({
    new: { label: t("newTasks"), color: "var(--play-accent)" },
    submitted: { label: t("submitted"), color: "var(--play-primary)" },
    graded: { label: t("graded"), color: "#38B000" },
  }[s]);

  return (
    <StudentShell>
      <h1 className="text-4xl font-black text-white glow-text flex items-center gap-3"><Inbox size={30} />{t("myTasks")}</h1>

      <div className="grid md:grid-cols-2 gap-6 mt-8">
        {items.map((a, i) => {
          const b = badge(a.my_status);
          const isSheet = a.kind === "sheet";
          return (
            <motion.div key={a.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.06 }} className="play-card" data-testid={`task-card-${a.id}`}
              style={{ borderColor: b.color }}>
              <div className="flex justify-between items-start gap-3">
                <h3 className="text-xl font-extrabold">{a.title}</h3>
                <span className="rounded-full px-3 py-1 text-xs font-extrabold text-white shrink-0"
                  style={{ background: b.color }}>{b.label}</span>
              </div>
              {isSheet && (
                <span className="inline-flex items-center gap-1 text-xs font-bold mt-2"
                  style={{ color: "var(--play-accent)" }} data-testid={`task-sheet-badge-${a.id}`}>
                  <ListChecks size={13} />أسئلة متنوعة — تُحلّ داخل الموقع</span>
              )}
              <p className="text-sm text-white/75 mt-3 leading-relaxed">{a.instructions}</p>
              <div className="flex items-center gap-3 text-xs text-white/45 mt-3">
                <span>{a.teacher_name}</span>
                {a.due_date && <span className="flex items-center gap-1"><Clock size={12} />{a.due_date}</span>}
              </div>
              {a.my_status === "graded" ? (
                <div className="mt-4 rounded-xl p-3 text-sm" style={{ background: "rgba(62,196,109,.2)" }}
                  data-testid={`task-grade-${a.id}`}>
                  <b className="flex items-center gap-1"><CheckCircle2 size={15} />{t("score")}: {a.my_score}%</b>
                  <div className="text-white/75 mt-1">{a.my_feedback}</div>
                </div>
              ) : (
                <button onClick={() => { setTarget(a); setAnswer(""); setResult(null); }}
                  data-testid={`solve-btn-${a.id}`}
                  className="play-btn text-white w-full mt-5" style={{ background: b.color }}>
                  {a.my_status === "submitted" ? t("edit") : t("submit")}</button>
              )}
            </motion.div>
          );
        })}
        {!items.length && <div className="play-card text-white/45">{t("noData")}</div>}
      </div>

      <Modal open={!!target} onClose={() => { setTarget(null); setResult(null); }}
        title={target?.title || ""} testid="solve-modal">
        {target?.kind === "sheet" && target?.sheet ? (
          result ? (
            <div className="space-y-3" data-testid="sheet-result">
              <p className="text-lg font-black" style={{ color: "#3ec46d" }}>
                {result.auto_total ? `${result.auto_correct} من ${result.auto_total} صحيحة`
                  : "تم التسليم"}</p>
              {!!result.pending_manual && (
                <p className="text-sm text-white/70">
                  {result.pending_manual} سؤال يحتاج تصحيح المعلم ✍️</p>
              )}
              <button onClick={() => { setTarget(null); setResult(null); }}
                className="play-btn text-white w-full" data-testid="sheet-result-close"
                style={{ background: "var(--play-primary)" }}>تمام</button>
            </div>
          ) : (
            <SheetSolver sheet={target.sheet} onSubmit={submitSheet} busy={busy} testid="task-solver" />
          )
        ) : (
          <>
            <p className="text-sm text-white/75 mb-3">{target?.instructions}</p>
            <form onSubmit={submit} className="space-y-3">
              <textarea rows="8" className="input" placeholder={t("answerHere")} required data-testid="task-answer-input"
                value={answer} onChange={(e) => setAnswer(e.target.value)} />
              <button className="play-btn text-white w-full" style={{ background: "var(--play-primary)" }}
                data-testid="task-submit-btn">{t("submit")}</button>
            </form>
          </>
        )}
      </Modal>
    </StudentShell>
  );
}
